import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import ResultsView from "@/components/ResultsView";
import QuoteEditor from "@/components/QuoteEditor";
import QuoteChatRoom from "@/components/QuoteChatRoom";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Trash2, Pencil, Eye, MessageSquare, ShieldCheck, CheckCircle2 } from "lucide-react";

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("view"); // "view" | "edit"
  const [chatQuoteId, setChatQuoteId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/quotes")
      .then((r) => setQuotes(r.data))
      .catch(() => toast.error("Échec du chargement"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const refreshSelected = (id) => {
    if (!id) return;
    api.get(`/quotes/${id}`).then((r) => setSelected(r.data)).catch(() => {});
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce devis ?")) return;
    try {
      await api.delete(`/quotes/${id}`);
      toast.success("Devis supprimé");
      if (selected?.id === id) { setSelected(null); setMode("view"); }
      load();
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  const onUpdated = (fresh) => {
    setSelected(fresh);
    load();
  };

  const toggleClientValidated = async () => {
    if (!selected) return;
    const willValidate = !selected.client_validated;
    let note = null;
    if (willValidate) {
      note = window.prompt(
        "Note optionnelle (ex. n° de PO, date de signature)…",
        ""
      );
      if (note === null) return; // user cancelled
    } else if (!window.confirm("Retirer la validation client de ce devis ?")) {
      return;
    }
    try {
      const r = await api.post(`/quotes/${selected.id}/validate-client`, {
        validated: willValidate,
        note: note || null,
      });
      setSelected(r.data);
      load();
      toast.success(willValidate ? "Devis marqué comme validé par le client." : "Validation client retirée.");
    } catch {
      toast.error("Échec de la mise à jour.");
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-12">
      <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 mb-6 sm:mb-8">Mes devis</h1>

      <div className="grid lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4">
          <div className="border border-zinc-200 bg-white rounded-sm" data-testid="quotes-list">
            {loading ? (
              <div className="p-6 text-sm text-zinc-500">Chargement…</div>
            ) : quotes.length === 0 ? (
              <div className="p-6 text-sm text-zinc-500">Aucun devis. Générez-en un depuis le Générateur de BOM/BOQ.</div>
            ) : (
              <ul className="divide-y divide-zinc-100">
                {quotes.map((q) => (
                  <li
                    key={q.id}
                    onClick={() => { setSelected(q); setMode("view"); }}
                    data-testid={`quote-item-${q.id}`}
                    className={`group flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-zinc-50 ${selected?.id === q.id ? "bg-zinc-50 border-l-2 border-[#D42B1E]" : "border-l-2 border-transparent"}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-zinc-900 truncate">{q.title}</p>
                        {q.revisions_count > 1 && (
                          <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-zinc-900 text-white" data-testid={`quote-rev-badge-${q.id}`}>
                            V{q.revision}
                          </span>
                        )}
                        {q.review_status === "requested" && (
                          <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-900 border border-amber-300" title="Revue Fortinet en attente" data-testid={`quote-review-badge-${q.id}`}>
                            Revue
                          </span>
                        )}
                        {q.review_status === "resolved" && (
                          <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-green-100 text-green-900 border border-green-300" title="Revue Fortinet résolue">
                            ✓
                          </span>
                        )}
                        {q.client_validated && (
                          <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-emerald-600 text-white inline-flex items-center gap-0.5" title={`Validé par le client le ${new Date(q.client_validated_at).toLocaleDateString("fr-FR")}`} data-testid={`quote-validated-badge-${q.id}`}>
                            <CheckCircle2 className="h-2.5 w-2.5" /> Validé
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-zinc-500 font-mono mt-0.5">
                        {new Date(q.created_at).toLocaleDateString("fr-FR")} · ${q.configuration.total_cost.toLocaleString("fr-FR")}
                        {q.messages_count > 0 && (
                          <span className="ml-2 inline-flex items-center gap-0.5"><MessageSquare className="h-3 w-3" />{q.messages_count}</span>
                        )}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); del(q.id); }}
                      className="h-7 w-7 rounded-sm opacity-0 group-hover:opacity-100"
                      data-testid={`quote-delete-${q.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>

        <main className="lg:col-span-8">
          {selected ? (
            <div className="border-2 border-zinc-200 bg-zinc-50/30 rounded-sm overflow-hidden" data-testid="quote-detail-wrap">
              {/* Wrap header — quote identity, customer info, top-line KPIs */}
              <div className="bg-white border-b border-zinc-200 px-4 sm:px-5 py-3 flex flex-wrap items-center gap-3 justify-between" data-testid="quote-detail-header">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-zinc-400">
                    Devis · V{selected.revision}
                  </p>
                  <h2 className="text-base sm:text-lg font-semibold text-zinc-900 truncate" title={selected.title}>
                    {selected.title}
                  </h2>
                  {selected.customer_info && (
                    <p className="text-xs text-zinc-500 truncate mt-0.5">
                      {[
                        selected.customer_info.name,
                        selected.customer_info.country,
                        selected.customer_info.branch,
                      ].filter(Boolean).join(" · ") || "Client non renseigné"}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-mono font-semibold text-zinc-900 tabular-nums">
                    ${selected.configuration.total_cost.toLocaleString("fr-FR")}
                  </span>
                  {selected.review_status === "requested" && (
                    <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-900 border border-amber-300">
                      Revue Fortinet
                    </span>
                  )}
                  {selected.client_validated && (
                    <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-emerald-600 text-white inline-flex items-center gap-0.5">
                      <CheckCircle2 className="h-2.5 w-2.5" /> Validé client
                    </span>
                  )}
                </div>
              </div>

              {/* Wrap body — toolbar + ResultsView/QuoteEditor */}
              <div className="p-3 sm:p-4 space-y-4">
              {/* View / Edit toggle + chat */}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="inline-flex items-center rounded-sm border border-zinc-200 overflow-hidden text-[11px]" role="group">
                  <button
                    type="button"
                    onClick={() => setMode("view")}
                    className={`px-3 py-1.5 font-semibold uppercase tracking-wide transition-colors ${mode === "view" ? "bg-[#D42B1E] text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                    data-testid="quote-mode-view"
                  >
                    <Eye className="inline h-3 w-3 mr-1.5" /> Lecture
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("edit")}
                    className={`px-3 py-1.5 font-semibold uppercase tracking-wide transition-colors border-l border-zinc-200 ${mode === "edit" ? "bg-[#D42B1E] text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                    data-testid="quote-mode-edit"
                  >
                    <Pencil className="inline h-3 w-3 mr-1.5" /> Édition
                  </button>
                </div>
                <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant={selected.client_validated ? "default" : "outline"}
                  onClick={toggleClientValidated}
                  className={`rounded-sm ${selected.client_validated ? "bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600" : "border-zinc-200 hover:border-emerald-500 hover:text-emerald-700"}`}
                  data-testid="quote-validate-client-btn"
                  title={selected.client_validated ? `Validé le ${new Date(selected.client_validated_at).toLocaleDateString("fr-FR")} par ${selected.client_validated_by_email || "—"}` : "Marquer ce devis comme validé par le client"}
                >
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                  {selected.client_validated ? "Validé par le client" : "Valider par le client"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setChatQuoteId(selected.id)}
                  className="rounded-sm border-zinc-200 hover:border-[#D42B1E]/40 relative"
                  data-testid="quote-open-chat"
                >
                  <ShieldCheck className="h-3.5 w-3.5 mr-1.5 text-[#D42B1E]" />
                  Salon Fortinet
                  {selected.messages_count > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-[#D42B1E] text-white text-[10px] font-mono font-semibold">
                      {selected.messages_count}
                    </span>
                  )}
                  {selected.review_status === "requested" && (
                    <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                  )}
                </Button>
                </div>
              </div>

              {mode === "view" ? (
                <ResultsView result={selected.configuration} customerInfo={selected.customer_info} highlightBom quote={selected} />
              ) : (
                <QuoteEditor
                  quote={selected}
                  onUpdated={(fresh) => { onUpdated(fresh); refreshSelected(fresh.id); }}
                />
              )}
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-zinc-200 rounded-sm p-16 text-center bg-white">
              <p className="text-sm text-zinc-500">Sélectionnez un devis pour afficher les détails.</p>
            </div>
          )}
        </main>
      </div>

      {/* Chat sheet — Salon Fortinet attached to a quote */}
      <Sheet open={!!chatQuoteId} onOpenChange={(v) => !v && setChatQuoteId(null)}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-[520px] p-0 flex flex-col border-l-0 sm:border-l-4 sm:border-l-[#D42B1E]"
          data-testid="quote-chat-sheet"
        >
          <SheetTitle className="sr-only">Salon Fortinet</SheetTitle>
          {chatQuoteId && (
            <QuoteChatRoom
              quoteId={chatQuoteId}
              quote={quotes.find((q) => q.id === chatQuoteId)}
              onReviewStatusChange={() => {
                // Refresh both the quote list (sidebar badges) AND the
                // currently selected quote (Salon Fortinet header counter)
                // so the message count clears immediately after a reset.
                load();
                if (chatQuoteId) refreshSelected(chatQuoteId);
              }}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
